import React from "react";
import type { Tag as TagType } from "../../types/orderbook";

interface TagProps {
  tag: TagType;
  onRemove?: () => void;
  onClick?: () => void;
  variant?: "default" | "overlay";
  disabled?: boolean;
  className?: string;
}

// Convert hex to RGB
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

// Get darker version of a color for text
function getDarkerColor(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#1F2937"; // fallback dark gray
  
  // Darken by reducing each RGB value by 40%
  const r = Math.max(0, Math.floor(rgb.r * 0.4));
  const g = Math.max(0, Math.floor(rgb.g * 0.4));
  const b = Math.max(0, Math.floor(rgb.b * 0.4));
  
  return `#${[r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('')}`;
}

// Convert hex to rgba with opacity
function hexToRgba(hex: string, opacity: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(31, 41, 55, ${opacity})`; // fallback
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
}

export const Tag: React.FC<TagProps> = ({
  tag,
  onRemove,
  onClick,
  variant = "default",
  disabled = false,
  className = ""
}) => {
  const tagColor = tag.color;
  
  let baseClassName = "ob-tag";
  if (variant === "overlay") {
    baseClassName = "ob-tag ob-tag--overlay";
  }
  if (onClick && !disabled) {
    baseClassName += " ob-tag--clickable";
  }
  
  // Use color as background if provided, otherwise use default styling
  // For overlay variant, use the tag color with transparency
  const style: React.CSSProperties = tagColor ? {
    backgroundColor: variant === "overlay" 
      ? hexToRgba(tagColor, 0.9) // 90% opacity for overlay
      : tagColor,
    color: getDarkerColor(tagColor),
    border: "none"
  } : {};

  const handleClick = (e: React.MouseEvent) => {
    if (onClick && !disabled) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <span 
      className={`${baseClassName} ${className}`} 
      style={style}
      onClick={handleClick}
      role={onClick && !disabled ? "button" : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && !disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {tag.name}
      {onRemove && (
        <button
          type="button"
          className="ob-tag-remove"
          onClick={handleRemoveClick}
          aria-label={`Remove ${tag.name} tag`}
          disabled={disabled}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      )}
    </span>
  );
};
